import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { loadGuest, saveGuest, type GuestIdentity } from '../lib/guest';
import { getShareToken } from '../lib/api';
import { useConfig } from '../lib/session';
import { Dialog, Field } from './ui';

const GuestContext = createContext<() => Promise<GuestIdentity | null>>(async () => null);

/**
 * Gäste über Freigabe-Links haben kein Konto. Vor dem ersten Kommentar oder der ersten
 * Freigabe fragen wir einmal nach dem Namen und merken ihn uns im Browser.
 */
export function GuestProvider({ children }: { children: ReactNode }) {
  const { data: config } = useConfig();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const resolver = useRef<((value: GuestIdentity | null) => void) | null>(null);

  const ensure = useCallback(async () => {
    if (!getShareToken()) return null;
    const known = loadGuest();
    if (known) return known;
    setName('');
    setEmail('');
    setOpen(true);
    return new Promise<GuestIdentity | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const finish = (value: GuestIdentity | null) => {
    setOpen(false);
    resolver.current?.(value);
    resolver.current = null;
  };

  return (
    <GuestContext.Provider value={ensure}>
      {children}
      <Dialog
        open={open}
        onClose={() => finish(null)}
        title="Wer gibt Feedback?"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => finish(null)}>
              Abbrechen
            </button>
            <button
              className="btn btn-primary"
              disabled={!name.trim()}
              onClick={() => {
                const identity = { name: name.trim(), email: email.trim() || undefined };
                saveGuest(identity);
                finish(identity);
              }}
            >
              Weiter
            </button>
          </>
        }
      >
        <p className="muted">
          Dein Name erscheint bei deinen Kommentaren und Freigaben, damit{' '}
          {config?.branding.providerName || 'die Agentur'} weiß, von wem sie kommen.
        </p>
        <Field label="Name">
          {(id) => (
            <input
              id={id}
              className="input"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          )}
        </Field>
        <Field label="E-Mail (optional)">
          {(id) => (
            <input
              id={id}
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
            />
          )}
        </Field>
      </Dialog>
    </GuestContext.Provider>
  );
}

export const useGuest = () => useContext(GuestContext);
